import CardHeader from "components/CardHeader";
import FilterToggleButton from "components/FilterToggleButton";
import PageMeta from "components/PageMeta";
import usePersistentFilterSet from "common/usePersistentFilterSet";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Card, CardBody, Col, Row } from "reactstrap";
import { bindActionCreators } from "redux";
import * as api from "services/Api";
import { getInvoices } from "store/actions/Billing";
import { getTenants } from "store/actions/Tenants";
import { InvoiceState } from "store/reducers/Invoices";
import { RootState } from "../../store/store";
import InvoiceAddButton from "./InvoiceAddButton";
import InvoiceRowIndex from "./InvoiceRowIndex";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

interface Props {
    invoiceState: InvoiceState;
}

interface Actions {
    getTenants: () => void;
    getInvoices: () => void;
}

const DesktopInvoiceRowHeight = 84;
const MobileInvoiceRowHeight = 136;
const InvoiceListFallbackHeight = 640;
const InvoiceListOverscan = 6;
const MobileInvoiceMediaQuery = "(max-width: 767.98px)";

const isMobileInvoiceViewport = () =>
    typeof window.matchMedia === "function" && window.matchMedia(MobileInvoiceMediaQuery).matches;

type InvoiceStatus = "quote" | "draft" | "paid" | "unpaid" | "overdue";

const InvoiceStatuses: InvoiceStatus[] = ["quote", "draft", "paid", "unpaid", "overdue"];

export const matchesInvoiceSearch = (
    invoice: Invoice,
    matchingAccountIds: ReadonlySet<string>,
    keyword: string,
) => invoice.name.toLowerCase().includes(keyword)
    || (invoice.invoice_number || "").toLowerCase().includes(keyword)
    || matchingAccountIds.has(invoice.customer)
    || (invoice.supplier !== undefined && matchingAccountIds.has(invoice.supplier));

const getInvoiceStatus = (invoice: Invoice): InvoiceStatus => {
    if (invoice.quote) {
        return "quote";
    }
    if (invoice.draft) {
        return "draft";
    }
    if (invoice.IsPaid()) {
        return "paid";
    }
    if (invoice.IsPayWhenPaid()) {
        return "unpaid";
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return invoice.GetDeadlineDate().getTime() < today.getTime() ? "overdue" : "unpaid";
};

const Invoices: React.FC<Props & Actions> = ({ invoiceState, getTenants, getInvoices }) => {
    const { t } = useOfficeTranslation();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [nameKeyword, setNameKeyword] = useState("");
    const [visibleStatuses, toggleStatus] = usePersistentFilterSet(
        "galactic-filters-invoices-statuses",
        InvoiceStatuses,
    );
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(InvoiceListFallbackHeight);
    const [mobileRows, setMobileRows] = useState(isMobileInvoiceViewport);
    const listRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const searchTerm = new URL(window.location.href).searchParams.get("q");

        getInvoices();
        api.getAccounts().then(setAccounts);
        getTenants();
        if (searchTerm !== null) {
            setNameKeyword(searchTerm);
        }
    }, [getInvoices, getTenants]);

    useEffect(() => {
        if (typeof window.matchMedia !== "function") {
            return;
        }

        const media = window.matchMedia(MobileInvoiceMediaQuery);
        const update = () => setMobileRows(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    const matchingAccountIds = useMemo(() => {
        const keyword = nameKeyword.toLowerCase();

        return new Set(accounts
            .filter(account => account.legal_name.toLowerCase().includes(keyword))
            .map(account => account.id));
    }, [accounts, nameKeyword]);

    const filteredInvoices = useMemo(() => {
        const keyword = nameKeyword.toLowerCase();

        return invoiceState.getInvoices()
            .filter(invoice => matchesInvoiceSearch(invoice, matchingAccountIds, keyword))
            .sort((a, b) => b.GetCreatedDate().getTime() - a.GetCreatedDate().getTime());
    }, [invoiceState, matchingAccountIds, nameKeyword]);

    const statusCounts = useMemo(
        () => filteredInvoices.reduce<Record<InvoiceStatus, number>>(
            (counts, invoice) => {
                const status = getInvoiceStatus(invoice);
                counts[status] += 1;
                return counts;
            },
            { quote: 0, draft: 0, paid: 0, unpaid: 0, overdue: 0 },
        ),
        [filteredInvoices],
    );

    const visibleFilteredInvoices = useMemo(
        () => filteredInvoices.filter(invoice => visibleStatuses.has(getInvoiceStatus(invoice))),
        [filteredInvoices, visibleStatuses],
    );

    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) {
            return;
        }

        const updateViewportHeight = () => {
            setViewportHeight(listElement.clientHeight || InvoiceListFallbackHeight);
        };

        updateViewportHeight();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateViewportHeight);
            return () => window.removeEventListener("resize", updateViewportHeight);
        }

        const resizeObserver = new ResizeObserver(updateViewportHeight);
        resizeObserver.observe(listElement);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        const listElement = listRef.current;
        if (!listElement) {
            return;
        }

        listElement.scrollTop = 0;
        setScrollTop(0);
    }, [mobileRows, nameKeyword, visibleStatuses]);

    const invoiceRowHeight = mobileRows ? MobileInvoiceRowHeight : DesktopInvoiceRowHeight;
    const startIndex = Math.max(0, Math.floor(scrollTop / invoiceRowHeight) - InvoiceListOverscan);
    const visibleRowCount = Math.ceil(viewportHeight / invoiceRowHeight) + InvoiceListOverscan * 2;
    const endIndex = Math.min(visibleFilteredInvoices.length, startIndex + visibleRowCount);
    const visibleInvoices = visibleFilteredInvoices.slice(startIndex, endIndex);
    const topSpacerHeight = startIndex * invoiceRowHeight;
    const bottomSpacerHeight = Math.max(
        0,
        (visibleFilteredInvoices.length - endIndex) * invoiceRowHeight,
    );

    const updateSearch = (value: string) => {
        const url = new URL(window.location.href);
        if (value) {
            url.searchParams.set("q", value);
        } else {
            url.searchParams.delete("q");
        }
        window.history.replaceState({}, "", url);
        setNameKeyword(value);
    };

    return (
        <div className="page-content">
            <PageMeta title={t("common.invoices")} />

            <div className="page-title-box d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <h4 className="mb-1">{t("common.invoices")}</h4>
                    <p className="text-muted mb-0">{t("invoice.invoices.invoicesQuotesAndPaymentStatus")}</p>
                </div>
                <InvoiceAddButton />
            </div>

            <Card>
                <CardBody>
                    <CardHeader
                        title={t("invoice.invoices.invoiceOverview")}
                        icon="mdi-file-document-outline"
                        count={visibleFilteredInvoices.length}
                    />

                    <Row className="g-3 py-3 border-bottom">
                        <Col md={5}>
                            <div className="position-relative">
                                <input
                                    aria-label={t("invoice.invoices.searchInvoices")}
                                    className="form-control ps-5"
                                    placeholder={t("invoice.invoices.searchInvoiceOrAccount")}
                                    type="text"
                                    value={nameKeyword}
                                    onChange={event => updateSearch(event.target.value)}
                                />
                                <i
                                    className="mdi mdi-magnify position-absolute text-muted font-size-20"
                                    style={{ left: 16, top: 7 }}
                                />
                            </div>
                        </Col>
                        <Col md={7}>
                            <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-2 h-100">
                                <FilterToggleButton
                                    active={visibleStatuses.has("quote")}
                                    count={statusCounts.quote}
                                    label={t("invoice.invoices.quotes")}
                                    onToggle={() => toggleStatus("quote")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("draft")}
                                    count={statusCounts.draft}
                                    label={t("invoice.invoices.drafts")}
                                    onToggle={() => toggleStatus("draft")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("paid")}
                                    count={statusCounts.paid}
                                    label={t("common.paid")}
                                    onToggle={() => toggleStatus("paid")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("unpaid")}
                                    count={statusCounts.unpaid}
                                    label={t("invoice.invoices.unpaid")}
                                    onToggle={() => toggleStatus("unpaid")}
                                />
                                <FilterToggleButton
                                    active={visibleStatuses.has("overdue")}
                                    count={statusCounts.overdue}
                                    label={t("common.overdue")}
                                    onToggle={() => toggleStatus("overdue")}
                                />
                            </div>
                        </Col>
                    </Row>

                    {visibleFilteredInvoices.length > 0
                        ? <div
                            onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
                            ref={listRef}
                            style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto" }}
                        >
                            {topSpacerHeight > 0 &&
                                <div aria-hidden="true" style={{ height: topSpacerHeight }} />
                            }
                            {visibleInvoices.map(invoice =>
                                <InvoiceRowIndex
                                    accounts={accounts}
                                    invoice={invoice}
                                    key={invoice.id}
                                    style={{ height: invoiceRowHeight }}
                                />
                            )}
                            {bottomSpacerHeight > 0 &&
                                <div aria-hidden="true" style={{ height: bottomSpacerHeight }} />
                            }
                        </div>
                        : <p className="text-muted text-center mt-3 mb-0">{t("invoice.invoices.noInvoicesFound")}</p>
                    }
                </CardBody>
            </Card>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    invoiceState: state.Invoices,
});

const mapDispatchToProps = (dispatch: any) =>
    bindActionCreators({ getInvoices, getTenants }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Invoices);
